/**
 * Groups a flat service list into canonically-ordered category buckets.
 *
 * services  — array of service objects with at least { name, category, category_key }
 * categories — [{key, name, sort_order}] from the service_categories collection
 *              (returned by /sales/service-catalog, /service-library/dependency-options,
 *               /document-rules/meta, and /service-library/options as taxonomy_categories)
 *
 * Returns [{label, items}] sorted by canonical sort_order then label.
 * Services within each group are alphabetized by name.
 * Services with no recognized category appear under "Other" at the end.
 */
export function groupServices(services = [], categories = []) {
  const catOrder = {};
  const catLabel = {};
  categories.forEach(c => {
    catOrder[c.key] = typeof c.sort_order === 'number' ? c.sort_order : 9999;
    catLabel[c.key] = c.name;
  });

  const groups = {};
  for (const svc of services) {
    const key = svc.category_key || null;
    const label = key ? (catLabel[key] || svc.category || 'Other') : (svc.category || 'Other');
    const sort = key ? (catOrder[key] ?? 9999) : 9999;
    if (!groups[label]) groups[label] = { label, sort, items: [] };
    groups[label].items.push(svc);
  }

  for (const g of Object.values(groups)) {
    g.items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  return Object.values(groups).sort((a, b) =>
    a.sort !== b.sort ? a.sort - b.sort : a.label.localeCompare(b.label)
  );
}
